import React, { useState, useEffect } from 'react';
import ExcelJS from 'exceljs';
import { getUserRole, getCompanyId } from '../Auth';
import { sanitizeInput } from '../sanitize.js';
import { useLoading } from '../LoadingContext';
import '../../styles/UserManagementSection.css';

import { parseUserFile } from './UserUtil/fileParser';
import {
  fetchCompanies,
  fetchUsers,
  registerUser,
  bulkRegisterUsers,
  advanceUserProgress,
  toggleUserStatus,
  deleteUser
} from './UserUtil/userApi';
import {
  confirmDeliverable,
  handleUnconfirm,
  autoAdvanceUserIfReady,
  openDeliverableModal,
  fetchDeliverableStatuses
} from './UserUtil/userDeliverableLogic';
import DeliverableModal from './UserUtil/DeliverableModal';
import { ROLE } from './UserUtil/constants';
import UserAdvanceModal from './UserUtil/UserAdvanceModal';
import UserTable from './UserUtil/UserTable';

const UserManagementSection = ({ onPhotoView }) => {
  const { startLoading, finishLoading } = useLoading();
  const [users, setUsers] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const initialCompanyId = Number(getCompanyId()) || null;
  const [selectedCompanyId, setSelectedCompanyId] = useState(initialCompanyId);
  const [newUserName, setNewUserName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 40;

  const userRole = Number(getUserRole());
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [curriculumOptions, setCurriculumOptions] = useState([]);
  const [selectedCurriculum, setSelectedCurriculum] = useState('');

  const [deliverableModalData, setDeliverableModalData] = useState(null);
  const [deliverableStatuses, setDeliverableStatuses] = useState({});

  useEffect(() => {
    const loadAll = async () => {
      startLoading();
      try {
        if (userRole === ROLE.ADMIN) {
          const data = await fetchCompanies();
          setCompanyList(data.companies || []);
        }
        const usersData = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
        setUsers(usersData.users || []);
        setTotalPages(Math.ceil((usersData.total || 0) / itemsPerPage));
      } finally {
        finishLoading();
      }
    };
    loadAll();
  }, [selectedCompanyId, currentPage]);

  useEffect(() => {
    if (!selectedCompanyId && userRole !== ROLE.ADMIN) {
      const cid = Number(getCompanyId());
      if (cid) setSelectedCompanyId(cid);
    }
  }, [userRole, selectedCompanyId]);

  useEffect(() => {
    const initDeliverableStatuses = async () => {
      try {
        const userIds = users.map(u => u.id);
        const res = await fetchDeliverableStatuses(userIds);
        if (res.statuses) {
          setDeliverableStatuses(res.statuses);
        }
      } catch (err) {
        console.warn('📛 deliverableStatuses 初期化失敗', err);
      }
    };

    if (users.length > 0) {
      initDeliverableStatuses();
    }
  }, [users]);


  const resolvedCompanyId = userRole === ROLE.ADMIN ? selectedCompanyId : Number(getCompanyId());

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleRegisterUser = async () => {
    const name = sanitizeInput(newUserName);
    if (!name) return alert('利用者名を入力してください');
    const companyIdToSend = userRole === ROLE.ADMIN ? selectedCompanyId : initialCompanyId;
    if (!companyIdToSend) return alert('companyId が取得できません');

    startLoading();
    try {
      const res = await registerUser(name, companyIdToSend);
      if (!res.ok) {
        const err = await res.json();
        alert(err.message || '登録に失敗しました');
        return;
      }
      setNewUserName('');
      const data = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
      setUsers(data.users || []);
    } finally {
      finishLoading();
    }
  };

  const handleBulkRegisterFile = async (file) => {
    try {
      const list = await parseUserFile(file);
      if (list.length === 0) return alert('登録対象の利用者が見つかりません');
      await sendBulkRegister(list);
    } catch (err) {
      alert('ファイル解析に失敗しました');
    }
  };

  const sendBulkRegister = async (list) => {
    const companyIdToSend = userRole === ROLE.ADMIN ? selectedCompanyId : initialCompanyId;
    if (!companyIdToSend) return alert('companyId が取得できません');

    startLoading();
    try {
      const res = await bulkRegisterUsers(list, companyIdToSend);
      if (!res.ok) {
        const err = await res.json();
        alert(err.message || '一括登録に失敗しました');
        return;
      }
      const data = await res.json();
      const wb = new ExcelJS.Workbook();
      const sheet = wb.addWorksheet('利用者一覧');
      sheet.addRow(['利用者名', 'トークン (login_code)']);
      data.users.forEach(u => sheet.addRow([u.name, u.loginCode]));
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user_list.xlsx';
      a.click();
      alert('一括登録が完了しました');
      const refreshed = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
      setUsers(refreshed.users || []);
    } catch (err) {
      console.error('❌ bulk register error', err);
      alert('一括登録中にエラーが発生しました');
    } finally {
      finishLoading();
    }
  };

  const handleAdvanceProgress = (user) => {
    if (user.role >= ROLE.MANAGER) return;
    if (user.progress?.is_selectable) {
      setSelectedUser(user);
      setCurriculumOptions(user.progress.selectable_curriculums || []);
      setShowModal(true);
    } else {
      if (!window.confirm('次の回へ進めますか？')) return;
      sendAdvanceRequest(user.id, null);
    }
  };

  const sendAdvanceRequest = async (userId, nextCurriculum = null) => {
    startLoading();
    try {
      await advanceUserProgress(userId, nextCurriculum);
      const updated = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
      setUsers(updated.users || []);
      setShowModal(false);
    } finally {
      finishLoading();
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    startLoading();
    try {
      await toggleUserStatus(userId, currentStatus === 1 ? 0 : 1);
      const updated = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
      setUsers(updated.users || []);
    } finally {
      finishLoading();
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('利用者を削除しますか？')) return;
    startLoading();
    try {
      await deleteUser(userId);
      const updated = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
      setUsers(updated.users || []);
    } finally {
      finishLoading();
    }
  };

  const handleCompanyChange = (e) => {
    setSelectedCompanyId(e.target.value ? Number(e.target.value) : null);
    setCurrentPage(1);
  };

  const handleDragOver = e => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = e => { e.preventDefault(); setDragOver(false); };
  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length > 0) {
      setSelectedFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };
  const handleOpenDeliverables = async (user) => {
    const progress = user.progress;
    if (!progress) return;

    await openDeliverableModal(
      user.id,
      setDeliverableModalData,
      startLoading,
      finishLoading,
      progress.curriculum_name,
      progress.session_number
    );
  };

  const handleConfirmDeliverable = async (userId, curriculumName, sessionNumber) => {
    startLoading();
    try {
      await confirmDeliverable(userId, curriculumName, sessionNumber);
      await autoAdvanceUserIfReady(userId);
      const updatedUsers = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
      setUsers(updatedUsers.users || []);

      await openDeliverableModal(
        userId,
        setDeliverableModalData,
        startLoading,
        finishLoading,
        curriculumName,
        sessionNumber
      );
    } catch (err) {
      console.error('✅確認処理エラー:', err);
      alert('確認処理に失敗しました');
    } finally {
      finishLoading();
    }
  };

  const handleUnconfirmDeliverable = async (userId, curriculumName, sessionNumber) => {
    startLoading();
    try {
      await handleUnconfirm(userId, curriculumName, sessionNumber);
      const updatedUsers = await fetchUsers(resolvedCompanyId, currentPage, itemsPerPage);
      setUsers(updatedUsers.users || []);

      await openDeliverableModal(
        userId,
        setDeliverableModalData,
        startLoading,
        finishLoading,
        curriculumName,
        sessionNumber
      );
    } catch (err) {
      console.error('❌未確認処理エラー:', err);
      alert('未確認に戻す処理に失敗しました');
    } finally {
      finishLoading();
    }
  };

  return (
    <div className="user-management-section">
      {userRole === 9 && (
        <div>
          <h4>事業所選択</h4>
          <select value={selectedCompanyId || ''} onChange={handleCompanyChange}>
            <option value="">選択してください</option>
            {companyList.map((company) => (
              <option key={company.id} value={company.id}>
                {company.name}（{company.manager_token}）
              </option>
            ))}
          </select>
        </div>
      )}

      <h3>新規利用者登録</h3>
      <input placeholder="利用者名" value={newUserName} onChange={(e) => setNewUserName(e.target.value)} />
      <button onClick={handleRegisterUser}>登録</button>

      <h3>一括利用者登録（Excel または CSV）</h3>

      <div
        className={`upload-drop-area ${dragOver ? 'drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <p>ここにファイルをドラッグ＆ドロップ<br />または下からファイル選択</p>
      </div>
      <input
        type="file"
        accept=".xlsx,.csv"
        onChange={(e) => {
          setSelectedFile(e.target.files[0]);
        }}
      />
      <button
        onClick={() => {
          if (!selectedFile) {
            alert('ファイルを選択してください');
            return;
          }
          handleBulkRegisterFile(selectedFile);
        }}
        disabled={!selectedFile}
      >
        送信
      </button>

      <h3>利用者一覧</h3>
      <UserTable
        users={users}
        onAdvance={handleAdvanceProgress}
        onToggleStatus={handleToggleStatus}
        onDelete={handleDeleteUser}
        onPhotoView={onPhotoView}
        onOpenDeliverables={handleOpenDeliverables}
        deliverableStatuses={deliverableStatuses}
        setDeliverableStatuses={setDeliverableStatuses} 
      />

      {/* ページネーション */}
      <div className="pagination">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          前へ
        </button>
        {Array.from({ length: totalPages }, (_, i) => (
          <button key={i + 1} onClick={() => handlePageChange(i + 1)} className={currentPage === i + 1 ? 'active' : ''}>
            {i + 1}
          </button>
        ))}
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          次へ
        </button>
      </div>

      <UserAdvanceModal
        show={showModal}
        user={selectedUser}
        curriculumOptions={curriculumOptions}
        selectedCurriculum={selectedCurriculum}
        setSelectedCurriculum={setSelectedCurriculum}
        onSubmit={(curriculum) => sendAdvanceRequest(selectedUser.id, curriculum)}
        onCancel={() => setShowModal(false)}
      />

      <DeliverableModal
        show={!!deliverableModalData}
        onClose={() => setDeliverableModalData(null)}
        data={deliverableModalData}
        onConfirm={handleConfirmDeliverable}
        onUnconfirm={handleUnconfirmDeliverable}
      />
    </div>
  )};

export default UserManagementSection;